import type {
  DedupCandidate,
  ImportAddressFix,
  ImportRowError,
} from "@adva/types";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/v2/empty-state";
import { formatNormalizedAddress, pluralize } from "../labels";

interface AllChangesTabProps {
  fixes: ImportAddressFix[];
  candidates: DedupCandidate[];
  errors: ImportRowError[];
}

const PREVIEW_COUNT = 3;

export function AllChangesTab({
  fixes,
  candidates,
  errors,
}: AllChangesTabProps) {
  const total = fixes.length + candidates.length + errors.length;

  if (total === 0) {
    return (
      <EmptyState
        title="No changes to review"
        description="This import had no address corrections, duplicates, or errors."
      />
    );
  }

  return (
    <div className="space-y-4">
      {fixes.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="typo-label text-[color:var(--color-text-md)]">
              {pluralize(fixes.length, "address fix", "address fixes")}
            </h3>
          </div>
          <ul className="space-y-2">
            {fixes.slice(0, PREVIEW_COUNT).map((fix) => (
              <li
                key={fix.id}
                className="typo-body text-[color:var(--color-text-md)]"
              >
                <span className="font-mono">{fix.original_input}</span>
                <span className="mx-2 text-[color:var(--color-text-lo)]">
                  →
                </span>
                <span>{formatNormalizedAddress(fix)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {candidates.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="typo-label text-[color:var(--color-text-md)]">
              {pluralize(candidates.length, "duplicate pair")}
            </h3>
          </div>
          <ul className="space-y-2">
            {candidates.slice(0, PREVIEW_COUNT).map((c) => {
              const importedName =
                [c.imported_contact.first_name, c.imported_contact.last_name]
                  .filter(Boolean)
                  .join(" ") || "(imported)";
              const existingName =
                [c.existing_contact.first_name, c.existing_contact.last_name]
                  .filter(Boolean)
                  .join(" ") || "(existing)";
              return (
                <li
                  key={c.id}
                  className="typo-body text-[color:var(--color-text-md)]"
                >
                  <span className="tabular-nums">
                    {Math.round(c.confidence * 100)}%
                  </span>
                  <span className="mx-2 text-[color:var(--color-text-lo)]">
                    ·
                  </span>
                  <span>
                    {importedName} vs {existingName}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {errors.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="typo-label text-[color:var(--color-text-md)]">
              {pluralize(errors.length, "error")}
            </h3>
          </div>
          <ul className="space-y-2">
            {errors.slice(0, PREVIEW_COUNT).map((err) => (
              <li
                key={`${err.row}-${err.field}`}
                className="typo-body text-[color:var(--color-text-md)]"
              >
                <span className="tabular-nums">Row {err.row}</span>
                <span className="mx-2 text-[color:var(--color-text-lo)]">
                  ·
                </span>
                <span className="font-mono">{err.field}</span>
                <span className="mx-2 text-[color:var(--color-text-lo)]">
                  ·
                </span>
                <span>{err.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
